/**
 * Scroll locking for the app's real scroller.
 *
 * The sheets used to do `document.body.style.overflow = 'hidden'`. That
 * stopped working the moment the layout moved to a one-viewport-tall shell
 * with an internal scroller: the body no longer scrolls, so setting its
 * overflow locks nothing and the content behind an open sheet stayed
 * scrollable. Nothing errors — it just silently does nothing.
 *
 * The scroller is `.app-main` (flex:1, overflow:auto). `.app-shell` is
 * overflow:hidden and does not scroll — locking it would be a no-op.
 *
 * Locking is expressed as an attribute on <html> with the actual
 * `overflow: hidden` applied in CSS, so the rule lives next to the layout
 * it depends on rather than being hard-coded to one element here.
 *
 * A counter rather than a boolean, because a sheet can open on top of
 * another (a menu sheet over an action menu); the first component to close
 * must not release the lock while the second is still open.
 */

const ATTR = 'data-scroll-locked';
let depth = 0;

export function lockAppScroll(): void {
  depth += 1;
  if (depth === 1) document.documentElement.setAttribute(ATTR, 'true');
}

export function unlockAppScroll(): void {
  depth = Math.max(0, depth - 1);
  if (depth === 0) document.documentElement.removeAttribute(ATTR);
}

/** Test-only escape hatch so one test's lock cannot leak into the next. */
export function __resetScrollLock(): void {
  depth = 0;
  document.documentElement.removeAttribute(ATTR);
}

export function isAppScrollLocked(): boolean {
  return depth > 0;
}
